import * as tus from 'tus-js-client';

// Shared Bunny video upload helper. Extracted from VideoUploadPanel so the manager
// "Video Commentary" panel and the DynamicDetails "About the Manager(Video)" field
// share one implementation of the TUS direct-to-Bunny flow and one file-validation
// contract. The bytes are uploaded straight to Bunny via TUS — they never pass
// through the 3Skye server (no /tmp spool, no ALB idle-timeout hang).

const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500 MB — frontend guard only.
// NOTE: backend multipart caps are lower and set elsewhere — dev Main.java (100 MB)
// and prod Initialiser.java MultipartConfigElement (20 MB); align separately.
// Bunny-supported VIDEO container extensions (video only; audio/caption formats excluded).
export const ACCEPTED_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'm4p', 'mkv', 'mov', 'avi', 'flv', 'webm', 'wmv', 'ts', 'mpeg', 'amv', 'vod', '4mv'];
export const ACCEPTED_VIDEO_ACCEPT_ATTR = ACCEPTED_VIDEO_EXTENSIONS.map(e => '.' + e).join(',');

// Unwrap a StandardResponse ({ success, message, data }) down to its payload.
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

function getFileExtension(name) {
    const dot = (name || '').lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

// Returns an error message string, or null when the file is acceptable.
export function validateVideoFile(file) {
    if (!ACCEPTED_VIDEO_EXTENSIONS.includes(getFileExtension(file.name))) {
        return 'Unsupported file type. Please upload a video file.';
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        return `File is too large (max ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB).`;
    }
    return null;
}

// Mint a short-lived signed token, then upload the file DIRECTLY to Bunny via TUS.
//
//   remote   — a RemoteV2 instance (from useRemote()).
//   file     — the File to upload.
//   options  — { title, onProgress(sent, total), onStart(tusUpload) }.
//
// Resolves { guid, info } on success (info = the Bunny video record). Rejects on failure.
// The upload-complete audit call is fired for both outcomes. `onStart` receives the
// tus.Upload instance so callers can abort()/cleanup.
export function uploadVideoToBunny(remote, file, { title, onProgress, onStart } = {}) {
    return new Promise((resolve, reject) => {
        remote.post(`bunny/videos/tus-init?title=${encodeURIComponent(title || file.name)}`, {})
            .then(res => {
                const creds = unwrap(res);
                if (!creds || !creds.videoId) throw new Error('init failed');

                const upload = new tus.Upload(file, {
                    endpoint: creds.tusEndpoint,
                    retryDelays: [0, 3000, 5000, 10000, 20000],
                    chunkSize: 50 * 1024 * 1024, // 50 MB chunks → true mid-upload resume on large files
                    removeFingerprintOnSuccess: true,
                    headers: {
                        AuthorizationSignature: creds.signature,
                        AuthorizationExpire: String(creds.expiration),
                        VideoId: creds.videoId,
                        LibraryId: String(creds.libraryId),
                    },
                    metadata: { filetype: file.type, title: title || file.name },
                    onError: (error) => {
                        // Record the failed upload for the audit log.
                        remote.post(`bunny/videos/${creds.videoId}/upload-complete`, { success: false, sizeBytes: file.size, title, error: 'upload failed' }).catch(() => { /* non-fatal */ });
                        reject(error || new Error('upload failed'));
                    },
                    onProgress: (sent, total) => {
                        if (onProgress) onProgress(sent, total);
                    },
                    onSuccess: () => {
                        // Notify the backend so the upload is audit-logged. Transcription (when used)
                        // is handled by Bunny's library auto-transcribe setting.
                        remote.post(`bunny/videos/${creds.videoId}/upload-complete`, { success: true, sizeBytes: file.size, title }).catch(() => { /* non-fatal */ });
                        remote.get(`bunny/videos/${creds.videoId}`)
                            .then(infoRes => resolve({ guid: creds.videoId, info: unwrap(infoRes) }))
                            .catch(() => resolve({ guid: creds.videoId, info: null }));
                    },
                });
                if (onStart) onStart(upload);
                upload.start();
            })
            .catch(reject);
    });
}
